import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../../components/feature/SEOHead';
import ExercicioGratisNavbar from '../components/ExercicioGratisNavbar';
import LeadCaptureForm from '../components/LeadCaptureForm';

export default function PropositoVidaPage() {
  const [selectedReflections, setSelectedReflections] = useState<string[]>([]);
  const [purposeStatement, setPurposeStatement] = useState('');

  const reflections = [
    { id: 'paixao', label: 'O que me apaixona?', icon: 'ri-fire-line', color: 'bg-red-100 text-red-600' },
    { id: 'talento', label: 'Quais são meus talentos naturais?', icon: 'ri-star-line', color: 'bg-amber-100 text-amber-600' },
    { id: 'impacto', label: 'Como quero impactar o mundo?', icon: 'ri-earth-line', color: 'bg-blue-100 text-blue-600' },
    { id: 'valores', label: 'Quais são meus valores essenciais?', icon: 'ri-compass-3-line', color: 'bg-indigo-100 text-indigo-600' },
    { id: 'legado', label: 'Que legado quero deixar?', icon: 'ri-trophy-line', color: 'bg-purple-100 text-purple-600' },
    { id: 'contribuicao', label: 'Como posso servir aos outros?', icon: 'ri-hand-heart-line', color: 'bg-pink-100 text-pink-600' },
    { id: 'realizacao', label: 'O que me faz sentir realizado?', icon: 'ri-emotion-happy-line', color: 'bg-green-100 text-green-600' },
    { id: 'missao', label: 'Qual é minha missão de vida?', icon: 'ri-rocket-line', color: 'bg-teal-100 text-teal-600' },
  ];

  const toggleReflection = (id: string) => setSelectedReflections(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <SEOHead
        title="Descoberta do Propósito de Vida Grátis | Missão — Carta do Eu Futuro"
        description="Experimente gratuitamente a Descoberta do Propósito de Vida. Conecte-se com sua missão única no mundo através de reflexões guiadas, visualização criativa e PNL."
        keywords="propósito de vida, missão, autoconhecimento, PNL grátis, desenvolvimento pessoal"
        canonical="/exercicio-gratis/proposito-vida"
      />
      <ExercicioGratisNavbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-4 overflow-hidden">
        <div className="max-w-6xl mx-auto relative z-10 text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-semibold mb-6"><i className="ri-gift-line"></i>VERSÃO GRATUITA</div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent leading-tight">Descoberta do Propósito de Vida</h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">Conecte-se com sua missão de vida e descubra o caminho que traz significado, realização e impacto</p>
        </div>
      </section>

      {/* O que é */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-900">O que é a Descoberta do Propósito?</h2>
          <p className="text-lg leading-relaxed mb-6 text-gray-700">A <strong>Descoberta do Propósito de Vida</strong> combina reflexões guiadas, visualização criativa e PNL para revelar sua missão única no mundo.</p>
          <p className="text-lg leading-relaxed text-gray-700">Quando você vive alinhado com seu propósito, cada dia ganha significado, suas decisões se tornam mais claras e você experimenta realização profunda.</p>
        </div>
      </section>

      {/* Exercício */}
      <section className="py-16 px-4 bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl p-8 md:p-12">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-semibold mb-4"><i className="ri-gift-line"></i>EXPERIMENTE GRÁTIS</div>
              <h2 className="text-3xl font-bold mb-4 text-gray-900">Exercício Introdutório</h2>
            </div>
            <h3 className="text-xl font-bold mb-6 text-gray-900">Selecione as reflexões que mais ressoam com você:</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {reflections.map((reflection) => (
                <button key={reflection.id} onClick={() => toggleReflection(reflection.id)} className={`p-4 rounded-xl border-2 transition-all text-left cursor-pointer ${selectedReflections.includes(reflection.id) ? 'border-indigo-500 bg-indigo-50 scale-105' : 'border-gray-200 bg-white hover:border-indigo-300'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 ${reflection.color} rounded-lg flex items-center justify-center`}><i className={`${reflection.icon} text-xl`}></i></div>
                    <span className="font-semibold text-gray-900 text-sm">{reflection.label}</span>
                    {selectedReflections.includes(reflection.id) && <i className="ri-checkbox-circle-fill text-indigo-500 text-xl ml-auto"></i>}
                  </div>
                </button>
              ))}
            </div>
            <h3 className="text-xl font-bold mb-4 text-gray-900">Escreva uma frase sobre seu propósito:</h3>
            <textarea value={purposeStatement} onChange={(e) => setPurposeStatement(e.target.value)} placeholder="Ex: Meu propósito é inspirar pessoas a descobrirem seu potencial..." className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-indigo-500 outline-none text-gray-900 text-sm h-32 resize-none" maxLength={500} />
            <p className="text-xs text-gray-500 mt-2">{purposeStatement.length}/500 caracteres</p>
            {/* Bloqueados */}
            <div className="relative mt-8">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/60 to-white backdrop-blur-sm z-10 rounded-2xl flex items-center justify-center">
                <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white p-8 rounded-2xl text-center max-w-sm">
                  <i className="ri-vip-crown-line text-5xl mb-4 block"></i>
                  <h3 className="text-2xl font-bold mb-3">Desbloqueie o Exercício Completo</h3>
                  <p className="text-indigo-100 mb-6">7 passos completos + áudio guiado + workbook</p>
                  <Link to="/planos" className="bg-white text-indigo-600 px-8 py-4 rounded-xl font-bold hover:bg-indigo-50 transition-all whitespace-nowrap block">Ver Planos — A partir de R$ 18</Link>
                </div>
              </div>
              <div className="opacity-30 pointer-events-none space-y-4">
                {[3,4,5,6,7].map((n) => <div key={n} className="p-6 bg-gray-50 rounded-xl border-2 border-gray-200"><h3 className="font-bold text-gray-600">Passo {n} — bloqueado</h3></div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <LeadCaptureForm exercicioNome="Propósito de Vida" />

      {/* CTA Final */}
      <section className="py-20 px-4 bg-gradient-to-br from-charcoal-950 via-charcoal-900 to-charcoal-950 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-12 h-px bg-gold-500/30 mx-auto mb-8"></div>
          <i className="ri-compass-3-fill text-5xl mb-6 opacity-60 block text-gold-400"></i>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold mb-6 text-white">Pronto para Descobrir Seu Propósito de Vida?</h2>
          <p className="text-xl text-white/55 mb-8 max-w-2xl mx-auto font-['Montserrat'] font-light">Desbloqueie o exercício completo e comece sua jornada de descoberta hoje mesmo</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/planos" className="bg-gradient-to-r from-gold-500 to-gold-700 text-charcoal-900 px-10 py-5 rounded-xl font-bold hover:from-gold-600 hover:to-gold-800 transition-all whitespace-nowrap font-['Montserrat'] tracking-wide">Ver Planos e Preços</Link>
            <Link to="/" className="bg-white/8 text-white px-10 py-5 rounded-xl font-bold hover:bg-white/14 transition-all border border-gold-500/20 whitespace-nowrap font-['Montserrat'] tracking-wide">Voltar ao Início</Link>
          </div>
          <p className="text-white/30 text-sm mt-6 font-['Montserrat']">Garantia de 7 dias · Acesso vitalício</p>
        </div>
      </section>
    </div>
  );
}
